import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Context } from '../store/Store.js';
import { REDUCER_ACTION_TYPES } from '../store/Reducer.js';
import { INITIAL_FILE_STATE } from '../components/FinalReview/finalReviewHelpers.js';
import { useUser } from './userContext.js';

export const FinalReviewStateContext = createContext();
export const FinalReviewActionContext = createContext();

const FinalReviewContextProvider = ({ children }) => {
  const [loadFile, setLoadFile] = useState(false);
  const [state, dispatch] = useContext(Context);
  const [newUpload, setNewUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(INITIAL_FILE_STATE);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [trigger, setTrigger] = useState(false);
  const [openQuartileDialog, setOpenQuartileDialog] = useState(false);
  const [fileIsIllegal, setFileIsIllegal] = useState(true);
  const { user } = useUser();

  useEffect(() => {
    setLoadFile(false);
    setUploadedFile(INITIAL_FILE_STATE);
  }, [user?.activeOrganization, user?.activeBudget, user?.activeCostCenters]);

  const handleNewUpload = useCallback(() => {
    if (state.fireUnsavedChangesDialog) {
      setNewUpload(true);
      dispatch({ type: REDUCER_ACTION_TYPES.BACK_BUTTON_FIRED, payload: true });
      dispatch({ type: REDUCER_ACTION_TYPES.SET_USER_NAVIGATED, payload: !state.userNavigated });
      return;
    }

    setLoadFile(false);
    setUploadedFile(INITIAL_FILE_STATE);
    setOpenUploadDialog(true);
  }, [dispatch, state.fireUnsavedChangesDialog, state.userNavigated]);

  const stateValue = useMemo(() => ({
    loadFile,
    newUpload,
    uploadedFile,
    openUploadDialog,
    gridApi,
    trigger,
    openQuartileDialog,
    fileIsIllegal,
  }), [
    loadFile,
    newUpload,
    uploadedFile,
    openUploadDialog,
    gridApi,
    trigger,
    openQuartileDialog,
    fileIsIllegal,
  ]);

  const actionValue = useMemo(() => ({
    setLoadFile,
    setNewUpload,
    setUploadedFile,
    setOpenUploadDialog,
    setGridApi,
    setTrigger,
    setOpenQuartileDialog,
    setFileIsIllegal,
    handleNewUpload,
  }), [handleNewUpload]);

  return (
    <FinalReviewStateContext.Provider value={stateValue}>
      <FinalReviewActionContext.Provider value={actionValue}>
        {children}
      </FinalReviewActionContext.Provider>
    </FinalReviewStateContext.Provider>
  );
};

FinalReviewContextProvider.propTypes = {
  children: PropTypes.node,
};

export default FinalReviewContextProvider;

export const useFinalReviewState = () => {
  const context = useContext(FinalReviewStateContext);
  if (!context) throw new Error('useFinalReviewState must be used within FinalReviewContextProvider');
  return context;
};

export const useFinalReviewActions = () => {
  const context = useContext(FinalReviewActionContext);
  if (!context) throw new Error('useFinalReviewActions must be used within FinalReviewContextProvider');
  return context;
};
